'use client'

import React, { useState, useRef, useEffect } from 'react'
import Image from '@/components/Image'
import Link from 'next/link'
import {
  LucideIcon,
  Home,
  User,
  Users,
  FileText,
  Film,
  Bell,
  ShoppingCart,
  Settings,
  Plus,
  Shield,
  BarChart3,
  Heart,
  Star,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Search,
  Info,
  HelpCircle,
  Book,
  BookOpen,
  GraduationCap,
  Trophy,
  Target,
  Zap,
  Sparkles,
  Gift,
  Ticket,
  Camera,
  Video,
  Music,
  Image as ImageIcon,
  Folder,
  Download,
  Upload,
  Link as LinkIcon,
  ExternalLink,
  MessageCircle,
  MessageSquare,
  Send,
  Share2,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Award,
  Briefcase,
  DollarSign,
  CreditCard,
} from 'lucide-react'
import { cn } from '@/utilities/cn'
import { Button } from '@/components/Button'
import { getLinkUrl } from '@/fields/link'
import jeufiImage from '../../../resources/images/jeufi.png'
import dynamic from 'next/dynamic'

// The animated clapperboard is the help button's icon, on every page. It is built on framer-motion
// (~35 KB compressed), which made the library part of every page's critical JavaScript. Loaded
// after hydration instead; a same-size placeholder keeps the button from changing shape meanwhile.
const Clapperboard = dynamic(() => import('@/components/Clapperboard/component'), {
  ssr: false,
  loading: () => <div className="relative w-16 h-16" aria-hidden="true" />,
})

// Icon mapping for CMS links (same as NavDrawer)
const iconMap: Record<string, LucideIcon> = {
  Home,
  User,
  Users,
  FileText,
  Film,
  Bell,
  ShoppingCart,
  Settings,
  Plus,
  Shield,
  BarChart3,
  Heart,
  Star,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Search,
  Info,
  HelpCircle,
  Book,
  BookOpen,
  GraduationCap,
  Trophy,
  Target,
  Zap,
  Sparkles,
  Gift,
  Ticket,
  Camera,
  Video,
  Music,
  Image: ImageIcon,
  Folder,
  Download,
  Upload,
  Link: LinkIcon,
  ExternalLink,
  MessageCircle,
  MessageSquare,
  Send,
  Share2,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Award,
  Briefcase,
  DollarSign,
  CreditCard,
}

interface HelpMenuLink {
  link: {
    type?: 'reference' | 'custom' | null
    reference?: {
      value: string | { slug?: string | null } | null
    } | null
    url?: string | null
    label: string
    newTab?: boolean | null
    icon?: string | null
  }
  description?: string | null
  color?: 'slate' | 'ocean-blue' | 'primary' | 'sea-green' | 'accent' | 'red' | 'orange' | 'amber' | 'yellow' | 'lime' | 'green' | 'emerald' | 'teal' | 'cyan' | 'sky' | 'blue' | 'indigo' | 'violet' | 'purple' | 'fuchsia' | 'pink' | 'rose' | null
  highlighted?: boolean | null
}

interface FloatingHelpButtonClientProps {
  feedbackUrl?: string
  additionalLinks?: HelpMenuLink[]
}

// Color mapping for icon backgrounds and text
const getColorClasses = (color: string | null | undefined) => {
  // Default to slate if no color specified
  if (!color || color === 'slate') {
    return {
      iconBg: 'bg-slate-100 group-hover:bg-slate-200',
      iconText: 'text-slate-600',
    }
  }

  // Custom colors without shades (ocean-blue, primary, sea-green, accent)
  const customColors = ['ocean-blue', 'primary', 'sea-green', 'accent']
  if (customColors.includes(color)) {
    return {
      iconBg: `bg-${color}/10 group-hover:bg-${color}/20`,
      iconText: `text-${color}`,
    }
  }

  // Standard Tailwind colors with shades (red, blue, green, etc.)
  return {
    iconBg: `bg-${color}-500/10 group-hover:bg-${color}-500/20`,
    iconText: `text-${color}-600`,
  }
}

export function FloatingHelpButtonClient({
  feedbackUrl = '/feedback',
  additionalLinks = []
}: FloatingHelpButtonClientProps) {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  return (
    <div className="fixed z-[45] transition-all duration-1000 ease-in-out bottom-2" style={{left: 'calc(100% - 6rem)'}} ref={menuRef}>
      {/* Popup Menu */}
      {isOpen && (
        <div className="absolute bottom-20 right-0 mb-2 w-80 bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-5 fade-in duration-200">
          <div className="p-4 space-y-3">
            <h3 className="font-heading font-semibold text-lg text-slate-800 mb-2">
              Hoe kunnen we helpen?
            </h3>

            {/* FAQ Button */}
            <Link href="/veelgestelde-vragen" className="block">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-slate-50 transition-colors border border-slate-200 group"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-ocean-blue/10 flex items-center justify-center group-hover:bg-ocean-blue/20 transition-colors">
                    <svg
                      className="w-5 h-5 text-ocean-blue"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-800">FAQ</div>
                    <div className="text-sm text-slate-600">Veelgestelde vragen</div>
                  </div>
                </div>
              </button>
            </Link>

            {/* Contact Button */}
            <Link href="/contact" className="block">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-slate-50 transition-colors border border-slate-200 group"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <svg
                      className="w-5 h-5 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-800">Contact</div>
                    <div className="text-sm text-slate-600">Neem contact met ons op</div>
                  </div>
                </div>
              </button>
            </Link>

            {/* Report Bug/Suggestion Button */}
            <Link href={feedbackUrl} className="block">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-slate-50 transition-colors border border-slate-200 group"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-sea-green/10 flex items-center justify-center group-hover:bg-sea-green/20 transition-colors">
                    <svg
                      className="w-5 h-5 text-sea-green"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-800">Feedback</div>
                    <div className="text-sm text-slate-600">Meld een bug of suggestie</div>
                  </div>
                </div>
              </button>
            </Link>

            {/* Additional Links */}
            {additionalLinks.map((item, index) => {
              const url = getLinkUrl(item.link)
              const linkProps = item.link.newTab
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {}

              // Get icon from iconMap
              const iconName = item.link.icon
              const Icon = iconName ? iconMap[iconName] : null

              // Apply green background if highlighted
              const isHighlighted = item.highlighted === true

              // Get color classes based on selected color (or use sea-green if highlighted)
              const colorClasses = isHighlighted
                ? getColorClasses('sea-green')
                : getColorClasses(item.color || 'slate')

              return (
                <Link key={index} href={url} className="block" {...linkProps}>
                  <button
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      'w-full text-left px-4 py-3 rounded-lg transition-colors border group',
                      isHighlighted
                        ? 'bg-sea-green/10 border-sea-green/30 hover:bg-sea-green/20'
                        : 'border-slate-200 hover:bg-slate-50'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                        colorClasses.iconBg
                      )}>
                        {Icon ? (
                          <Icon className={cn('w-5 h-5', colorClasses.iconText)} />
                        ) : (
                          <Info className={cn('w-5 h-5', colorClasses.iconText)} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">{item.link.label}</div>
                        {item.description && (
                          <div className="text-sm text-slate-600 mt-0.5">{item.description}</div>
                        )}
                      </div>
                      {item.link.newTab && (
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </button>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'group relative w-16 h-16 transition-all duration-200 scale-100',
          'flex items-center justify-center',
          'hover:scale-105 drop-shadow-xl',
          isOpen && 'scale-105'
        )}
        aria-label="Help menu"
      >
        <Clapperboard className="relative w-16 h-16 rotate-1" isOpen={isOpen ? true : undefined} />

        {/* Pulse animation on hover */}
        {/*<div className="absolute inset-0 rounded-full bg-primary/20 animate-ping opacity-0 group-hover:opacity-75" />*/}
      </button>
    </div>
  )
}
